/**
 * Botón "Comprar ya": añade el producto (variante, cantidad y propiedades del
 * formulario) al carrito y lleva directamente al checkout.
 * Si la respuesta de Shopify es ambigua, comprueba el carrito antes de mostrar
 * un error: si el producto está dentro, continúa al checkout.
 */
class BuyNowButton extends HTMLElement {
  constructor() {
    super();
    this.handleClick = this.handleClick.bind(this);
    this.handlePageShow = this.handlePageShow.bind(this);
    this.observer = new MutationObserver(() => this.syncDisabled());
  }

  connectedCallback() {
    this.button = this.querySelector('.buy-now__button');
    this.errorEl = this.querySelector('.buy-now__error');
    this.form = this.closest('form');
    if (!this.button || !this.form) return;

    this.button.addEventListener('click', this.handleClick);
    window.addEventListener('pageshow', this.handlePageShow);

    this.addButton = this.form.querySelector('button[name="add"]');
    if (this.addButton) {
      this.syncDisabled();
      this.observer.observe(this.addButton, { attributes: true, attributeFilter: ['disabled'] });
    }
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.handleClick);
    window.removeEventListener('pageshow', this.handlePageShow);
    this.observer.disconnect();
  }

  get root() {
    return window.Shopify?.routes?.root || '/';
  }

  syncDisabled() {
    if (this.busy || !this.addButton) return;
    this.button.disabled = this.addButton.disabled;
  }

  setBusy(busy) {
    this.busy = busy;
    if (busy) {
      this.button.disabled = true;
      this.button.setAttribute('aria-busy', 'true');
    } else {
      this.button.removeAttribute('aria-busy');
      this.button.disabled = this.addButton ? this.addButton.disabled : false;
    }
  }

  showError(message) {
    if (!this.errorEl) return;
    this.errorEl.textContent = message || '';
    this.errorEl.hidden = !message;
  }

  handlePageShow(event) {
    if (event.persisted) {
      this.setBusy(false);
      this.showError('');
    }
  }

  goToCheckout() {
    window.location.assign(`${this.root}checkout`);
  }

  async variantIsInCart(variantId) {
    try {
      const response = await fetch(`${this.root}cart.js`, { headers: { Accept: 'application/json' } });
      const cart = await response.json();
      return (cart.items || []).some((item) => String(item.variant_id) === String(variantId));
    } catch (error) {
      return false;
    }
  }

  async handleClick(event) {
    event.preventDefault();
    event.stopPropagation();
    if (this.busy) return;
    if (!this.form.reportValidity()) return;

    this.showError('');
    this.setBusy(true);

    const formData = new FormData(this.form);
    const variantId = formData.get('id');
    let serverMessage = '';

    try {
      const response = await fetch(`${this.root}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData,
      });

      if (response.ok) {
        this.goToCheckout();
        return;
      }

      const data = await response.json().catch(() => ({}));
      serverMessage = data.description || data.message || '';
      console.error('[buy-now] cart/add', response.status, data);
    } catch (error) {
      console.error('[buy-now]', error);
    }

    if (variantId && (await this.variantIsInCart(variantId))) {
      this.goToCheckout();
      return;
    }

    this.showError(serverMessage || this.dataset.error);
    this.setBusy(false);
  }
}

if (!customElements.get('buy-now-button')) {
  customElements.define('buy-now-button', BuyNowButton);
}